// src/pages/VolunteerOpportunityForm.js
// Updated: 08/25/2025 08:25 PM

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import {
    collection, doc, getDoc, addDoc, updateDoc, serverTimestamp,
    query, where, getDocs, writeBatch
} from 'firebase/firestore';
import {
    Container, Typography, TextField, Button, Box, CircularProgress,
    FormControl, InputLabel, Select, MenuItem, Grid,
    Card, CardContent, IconButton, Alert
} from '@mui/material';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

function VolunteerOpportunityForm() {
    const { opportunityId } = useParams();
    const { userProfile, currentUser } = useAuth();
    const navigate = useNavigate();
    const isEditing = !!opportunityId;

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState(null);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null); // State for the optional end time
    const [contactPersonId, setContactPersonId] = useState('');
    const [positions, setPositions] = useState([{ name: '', description: '', required: 1 }]);
    const [allChurchMembers, setAllChurchMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [isAuthorized, setIsAuthorized] = useState(false);

    useEffect(() => {
        const fetchMembers = async () => {
            if (!userProfile?.churchId) return;
            const membersQuery = query(
              collection(db, 'users'),
              where('churchId', '==', userProfile.churchId)
            );
            const membersSnapshot = await getDocs(membersQuery);
            setAllChurchMembers(membersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        };

        const fetchOpportunityAndAuthorize = async () => {
            const isLeadership = userProfile?.role === 'Leadership' || userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator';
            
            if (isEditing) {
                const opportunityRef = doc(db, 'churches', userProfile.churchId, 'volunteerOpportunities', opportunityId);
                const opportunitySnap = await getDoc(opportunityRef);
                if (opportunitySnap.exists()) {
                    const data = opportunitySnap.data();

                    if (isLeadership || data.contactPersonId === currentUser.uid) {
                        setIsAuthorized(true);
                        const opportunityDate = data.date?.toDate();
                        const opportunityEndDate = data.endTime?.toDate(); // Get end time
                        setTitle(data.title);
                        setDescription(data.description);
                        setDate(opportunityDate);
                        setStartTime(opportunityDate);
                        setEndTime(opportunityEndDate || null); // Set end time state
                        setContactPersonId(data.contactPersonId);

                        const positionsSnap = await getDocs(collection(opportunityRef, 'positions'));
                        if (!positionsSnap.empty) {
                            setPositions(positionsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
                        } else {
                            setPositions([]);
                        }
                    } else {
                        setIsAuthorized(false);
                        navigate('/volunteer');
                    }
                } else {
                    navigate('/volunteer');
                }
            } else {
                if (isLeadership) {
                    setIsAuthorized(true);
                } else {
                    setIsAuthorized(false);
                    navigate('/volunteer');
                }
            }
            setLoading(false);
        };

        if (userProfile?.churchId && currentUser?.uid) {
            fetchMembers();
            fetchOpportunityAndAuthorize();
        }
    }, [opportunityId, isEditing, userProfile, currentUser, navigate]);

    const handlePositionChange = (index, field, value) => {
        const newPositions = [...positions];
        newPositions[index][field] = value;
        setPositions(newPositions);
    };

    const handleAddPosition = () => {
        setPositions([...positions, { name: '', description: '', required: 1 }]);
    };

    const handleRemovePosition = (index) => {
        const newPositions = [...positions];
        newPositions.splice(index, 1);
        setPositions(newPositions);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');

        if (!title || !date || !startTime || !contactPersonId || positions.some(p => !p.name || p.required < 1)) {
            setError('Please fill in all required fields (Title, Date, Start Time, Contact Person) and ensure positions have a name and required count of at least 1.');
            setSubmitting(false);
            return;
        }
        
        let finalStartDate = new Date(date);
        finalStartDate.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);

        let finalEndDate = null;
        if (endTime) {
            finalEndDate = new Date(date);
            finalEndDate.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);
        }

        const opportunityData = {
            title,
            description,
            date: finalStartDate,
            endTime: finalEndDate, // Save the end time
            contactPersonId,
            contactPersonName: allChurchMembers.find(m => m.id === contactPersonId)?.firstName + ' ' + allChurchMembers.find(m => m.id === contactPersonId)?.lastName,
            churchId: userProfile.churchId,
            status: 'open',
            showOnMainCalendar: true,
        };

        try {
            if (isEditing) {
                const opportunityRef = doc(db, 'churches', userProfile.churchId, 'volunteerOpportunities', opportunityId);
                await updateDoc(opportunityRef, {
                    ...opportunityData,
                    updatedAt: serverTimestamp(),
                });

                const existingPositions = await getDocs(collection(opportunityRef, 'positions'));
                const existingPositionIds = existingPositions.docs.map(d => d.id);
                const newPositionIds = positions.map(p => p.id).filter(Boolean);
                
                const batch = writeBatch(db);

                const positionsToDelete = existingPositionIds.filter(id => !newPositionIds.includes(id));
                for (const posId of positionsToDelete) {
                    batch.delete(doc(opportunityRef, 'positions', posId));
                }

                for (const position of positions) {
                    if (position.id) {
                        batch.update(doc(opportunityRef, 'positions', position.id), position);
                    } else {
                        const positionRef = doc(collection(opportunityRef, 'positions'));
                        batch.set(positionRef, { ...position, signedUpUids: [] });
                    }
                }
                await batch.commit();

            } else {
                const newOpportunityRef = await addDoc(collection(db, 'churches', userProfile.churchId, 'volunteerOpportunities'), opportunityData);
                const batch = writeBatch(db);
                for (const position of positions) {
                    const positionRef = doc(collection(newOpportunityRef, 'positions'));
                    batch.set(positionRef, { ...position, signedUpUids: [] });
                }
                await batch.commit();
            }

            navigate('/volunteer');
        } catch (e) {
            console.error("Error saving volunteer opportunity:", e);
            setError('Failed to save the volunteer opportunity. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <CircularProgress />;
    }

    if (!isAuthorized) {
        return (
            <Container>
                <Alert severity="error">You are not authorized to edit this opportunity.</Alert>
            </Container>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Container maxWidth="md">
            <Typography variant="h4" gutterBottom>{isEditing ? 'Edit Volunteer Opportunity' : 'Create Volunteer Opportunity'}</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box component="form" onSubmit={handleSubmit}>
                <TextField
                    label="Opportunity Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    fullWidth required margin="normal"
                />
                <TextField
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth multiline rows={4} margin="normal"
                />
                <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 1 }}>
                    <DatePicker
                        label="Date of Opportunity"
                        value={date}
                        onChange={(newValue) => setDate(newValue)}
                        slots={{ textField: (params) => <TextField {...params} fullWidth required /> }}
                        enableAccessibleFieldDOMStructure={false}
                    />
                    <TimePicker
                        label="Start Time"
                        value={startTime}
                        onChange={(newValue) => setStartTime(newValue)}
                        slots={{ textField: (params) => <TextField {...params} fullWidth required /> }}
                        enableAccessibleFieldDOMStructure={false}
                    />
                    {/* *** FIX: Add the optional End Time picker *** */}
                    <TimePicker
                        label="End Time (Optional)"
                        value={endTime}
                        onChange={(newValue) => setEndTime(newValue)}
                        slots={{ textField: (params) => <TextField {...params} fullWidth /> }}
                        enableAccessibleFieldDOMStructure={false}
                    />
                </Box>
                <FormControl fullWidth margin="normal" required>
                    <InputLabel>Contact Person</InputLabel>
                    <Select
                        value={contactPersonId}
                        label="Contact Person"
                        onChange={(e) => setContactPersonId(e.target.value)}
                    >
                        {allChurchMembers.map(member => (
                            <MenuItem key={member.id} value={member.id}>{member.firstName} {member.lastName}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Positions</Typography>
                {positions.map((position, index) => (
                    <Card variant="outlined" sx={{ mb: 2 }} key={index}>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Position Name"
                                        value={position.name}
                                        onChange={(e) => handlePositionChange(index, 'name', e.target.value)}
                                        fullWidth required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Required Volunteers"
                                        type="number"
                                        value={position.required}
                                        onChange={(e) => handlePositionChange(index, 'required', e.target.value)}
                                        fullWidth required
                                        InputProps={{ inputProps: { min: 1 } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                    {positions.length > 1 && (
                                        <IconButton onClick={() => handleRemovePosition(index)} color="error">
                                            <RemoveCircleOutlineIcon />
                                        </IconButton>
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Description (Optional)"
                                        value={position.description}
                                        onChange={(e) => handlePositionChange(index, 'description', e.target.value)}
                                        fullWidth multiline rows={2}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                ))}
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddPosition} sx={{ mb: 3 }}>
                    Add Position
                </Button>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button onClick={() => navigate('/volunteer')} sx={{ mr: 2 }}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={submitting}>
                        {isEditing ? 'Save Changes' : 'Create Opportunity'}
                    </Button>
                </Box>
            </Box>
        </Container>
        </LocalizationProvider>
    );
}

export default VolunteerOpportunityForm;